import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group relative bg-background-900 rounded-xl overflow-hidden border border-background-700/30 hover:border-primary-500/30 transition-all duration-300 block cursor-pointer"
      >
        <div className="relative w-full h-[240px] md:h-[320px] overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/40 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
            <span className="inline-block px-3 py-1 text-xs font-medium text-primary-400 bg-primary-500/10 rounded-full mb-3">
              {article.category}
            </span>
            <h3 className="text-base md:text-lg font-bold text-foreground-50 group-hover:text-primary-400 transition-colors line-clamp-2">
              {article.title}
            </h3>
            <p className="text-sm text-foreground-400 mt-2 line-clamp-2">{article.excerpt}</p>
            <div className="flex items-center gap-3 mt-3 text-xs text-foreground-500">
              <span>{article.date}</span>
              <span>·</span>
              <span>{article.readTime}</span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex items-start gap-3 py-3 border-b border-background-700/20 last:border-0 cursor-pointer"
      >
        <div className="w-20 h-16 shrink-0 rounded-lg overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="min-w-0">
          <h4 className="text-sm font-medium text-foreground-200 group-hover:text-primary-400 transition-colors line-clamp-2">
            {article.title}
          </h4>
          <span className="text-xs text-foreground-500 mt-1 block">{article.date}</span>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group bg-background-900 rounded-xl overflow-hidden border border-background-700/30 hover:border-primary-500/30 transition-all duration-300 hover:translate-y-[-2px] block cursor-pointer"
    >
      <div className="relative w-full h-[180px] overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-4">
        <span className="inline-block px-2.5 py-0.5 text-[10px] font-medium text-primary-400 bg-primary-500/10 rounded-full mb-2">
          {article.category}
        </span>
        <h3 className="text-sm font-semibold text-foreground-50 group-hover:text-primary-400 transition-colors line-clamp-2">
          {article.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-2 line-clamp-2">{article.excerpt}</p>
        <div className="flex items-center gap-2 mt-3 text-[11px] text-foreground-600">
          <span>{article.date}</span>
          <span>·</span>
          <span>{article.readTime}</span>
        </div>
      </div>
    </Link>
  );
}